import React, { useEffect, useState } from 'react';
import { IInvoiceFullObject } from '../../Interfaces/IInvoice';
import { IDocumentDownloaded } from '../../Interfaces/IDocumentDownloaded';
import { DocumentDownloadedDialog } from '../../Document/DocumentAssistant/DocumentDownloadedDialog';
import { DocumentPreviewDialog } from '../../Document/DocumentAssistant/DocumentPreviewDialog';
import {
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { IDocument } from '../../Interfaces/IDocument';
import { useSidepanelDocumentContext } from '../LegalTransactionEdit';
import { getFetch } from '../../hooks/useFetch';
import {
  Delete,
  Download,
  FindInPage,
  Link,
  Search,
  PictureInPicture,
} from '@mui/icons-material';
import { IMIMEType } from '../../Interfaces/IMIMEType';
import { State } from '../../redux/mainReducer';
import { useSelector } from 'react-redux';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalTransactionType } from '../../Interfaces/ILegalTransactionType';
import { InvoiceStateChip } from '../../Invoice/InvoiceOverview';
import { DebtorAutocomplete } from '../../Invoice/DebtorAutocomplete';
import { DebtorWarningBars } from '../../Invoice/DebtorWarningBars';
import { IinvoiceState } from '../../Interfaces/IInvoiceState';
import { ILegalRole } from '../../Interfaces/ILegalRole';
import { ILegalTransactionRecordNumber } from '../../Interfaces/ILegalTransactionRecordNumber';

interface IProps {
  invoiceObject: IInvoiceFullObject;
  invoiceObjectArray: IInvoiceFullObject[];
  setInvoiceObjectArray: Function;
  downloadedArray?: IDocumentDownloaded[];
  assignedLegalClients: ILegalClient[];
  setLegalClients: Function;
  legalTransactionTypes: ILegalTransactionType[];
  legalRoles: ILegalRole[];
  invoiceStates: IinvoiceState[] | undefined;
  isLoadinginvoiceStates: boolean;
  recordNumberArray: ILegalTransactionRecordNumber[];
}

const base64Download = (
  localDocumentObject: IDocument,
  mimeTypes: IMIMEType[]
) => {
  const currentMimeType = mimeTypes.find(
    x => x.idMIMEType === localDocumentObject.idMIMEType
  );
  if (localDocumentObject.Data && currentMimeType) {
    const byteCharacters = atob(localDocumentObject.Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: currentMimeType.MIMEType });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = localDocumentObject.FileName;
    a.click();
  }
};

export const InvoiceRow: React.FC<IProps> = ({
  invoiceObject,
  invoiceObjectArray,
  setInvoiceObjectArray,
  downloadedArray,
  assignedLegalClients,
  setLegalClients,
  legalTransactionTypes,
  invoiceStates,
  isLoadinginvoiceStates,
  recordNumberArray,
}) => {
  const scopes = localStorage.getItem('scope')?.split(',') ?? [];
  const { setSelectedSidepanelDocument } = useSidepanelDocumentContext();
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );

  const [invoice, setInvoice] = useState(invoiceObject);
  const [localDocumentObject, setLocalDocumentObject] = useState<IDocument>({
    idDocument: invoice.idDocument,
    Data: invoice.Data,
    FileName: invoice.FileName,
    Created_at: new Date().toISOString(),
    idMIMEType: invoice.idMIMEType,
    idDocumentDirectory: null,
    idLegalTransaction: null,
    perEmail: null,
    isFromTemplate: null,
  });

  const [isOpenDownloaded, setIsOpenDownloaded] = useState(false);
  const [isOpenPreview, setisOpenPreview] = useState(false);

  // Spaltenanzahl der Tabelle (siehe InvoiceTable-Header): Nr., Art/Urkunden-
  // nummer, Status, Zahlungsschuldner/Summe, Dateiname, Aktionen = 6, plus die
  // optionale „Runter."-Spalte. Für den colSpan der Warnleiste (#1183).
  const columnCount = 6 + (downloadedArray ? 1 : 0);

  // Aufgelöste Schuldner-Objekte dieser Rechnung (für die Warnleisten #1183/#1185).
  const selectedDebtors = assignedLegalClients.filter(client =>
    invoice.LegalClients.includes(client.idLegalClient)
  );

  useEffect(() => {
    setInvoiceObjectArray([
      ...invoiceObjectArray.map(x =>
        x.idInvoice === invoice.idInvoice ? invoice : x
      ),
    ]);
  }, [invoice]);

  const handleOpenPreview = () => {
    if (!localDocumentObject.Data) {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        setLocalDocumentObject
      );
    }
    setisOpenPreview(true);
  };

  const handleRemove = () => {
    setInvoiceObjectArray([
      ...invoiceObjectArray.filter(x => x.idInvoice !== invoice.idInvoice),
    ]);
  };

  const handleDownload = async () => {
    if (localDocumentObject.Data) {
      base64Download(localDocumentObject, mimeTypeArray);
    } else {
      getFetch(
        '/document/',
        localDocumentObject.idDocument,
        (localDocumentObject: IDocument) => {
          setLocalDocumentObject(localDocumentObject);
          base64Download(localDocumentObject, mimeTypeArray);
        }
      );
    }
  };

  return (
    <>
      <DocumentDownloadedDialog
        open={isOpenDownloaded}
        onClose={() => setIsOpenDownloaded(false)}
        localDocumentObject={localDocumentObject}
        assignedLegalClients={assignedLegalClients}
      />
      <DocumentPreviewDialog
        open={isOpenPreview}
        localDocumentObject={localDocumentObject}
        onClose={() => setisOpenPreview(false)}
      />

      {/* #1183 + #1185: Warnleisten (kein Schuldner / Schuldner ohne bzw. mit
          deaktivierter E-Mail) über die volle Tabellenbreite, direkt über der
          Rechnung. Reaktiv über invoice.LegalClients. */}
      <DebtorWarningBars
        debtors={selectedDebtors}
        layout='tableRow'
        colSpan={columnCount}
      />

      <TableRow>
        {/* Nr. */}
        <TableCell sx={{ width: 72 }}>
          {invoice.idInvoice < 0 ? 'Neu' : invoice.idInvoice}
        </TableCell>

        {/* Runter. */}
        {downloadedArray && (
          <TableCell sx={{ width: 72 }}>
            {
              downloadedArray.filter(
                d => d.idDocument === localDocumentObject.idDocument
              ).length
            }
          </TableCell>
        )}

        {/* Art + Aktenzeichen untereinander */}
        <TableCell sx={{ width: 260 }}>
          <CustomeTextField
            label='Art'
            attr='idLegalTransactionType'
            object={invoice}
            setObject={setInvoice}
            type='select'
            maxWidth={240}
          >
            {legalTransactionTypes.map(type => (
              <MenuItem
                key={`type-${type.idLegalTransactionType}`}
                value={type.idLegalTransactionType}
              >
                {type.LegalTransactionType}
              </MenuItem>
            ))}
          </CustomeTextField>

          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth size='small'>
              <InputLabel id='recordnumber-label' shrink>
                Urkundennummer
              </InputLabel>
              <Select
                labelId='recordnumber-label'
                label='Urkundennummer'
                displayEmpty
                notched
                value={invoice.idLegalTransactionRecordNumber ?? ''}
                onChange={e => {
                  const value = e.target.value as number;
                  setInvoice({
                    ...invoice,
                    idLegalTransactionRecordNumber: value,
                  });
                }}
              >
                {recordNumberArray
                  // Leere Einträge ausblenden (z.B. der Default-Init-Eintrag),
                  // aber neu hinzugefügte mit Inhalt + negativer tempId zulassen,
                  // damit Rechnungen direkt darauf referenziert werden können.
                  .filter(rn => (rn.RecordNumberEntry ?? '').trim() !== '')
                  .map(rn => {
                    const isNew = (rn.idLegalTransactionRecordNumber ?? 0) < 0;
                    return (
                      <MenuItem
                        key={`rn-${rn.idLegalTransactionRecordNumber}`}
                        value={rn.idLegalTransactionRecordNumber}
                      >
                        {rn.RecordNumberEntry}
                        {isNew ? ' (neu, ungespeichert)' : ''}
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Box>
        </TableCell>

        {/* Status */}
        <TableCell sx={{ width: 120 }}>
          {isLoadinginvoiceStates ? (
            <CircularProgress size={20} />
          ) : invoiceStates === undefined ? (
            <>Fehler</>
          ) : (
            <InvoiceStateChip
              idInvoiceState={invoice.idInvoiceState}
              invoiceStates={invoiceStates}
            />
          )}
        </TableCell>

        {/* Schuldner + Summe */}
        <TableCell sx={{ width: 420 }}>
          <DebtorAutocomplete
            options={assignedLegalClients}
            value={assignedLegalClients.filter(c =>
              invoice.LegalClients.includes(c.idLegalClient)
            )}
            onChange={selected => {
              const ids = selected.map(c => c.idLegalClient);
              setInvoice({ ...invoice, LegalClients: ids });
              setLegalClients([
                ...assignedLegalClients.map(client => ({
                  ...client,
                  enableInvoiceReminding: ids.includes(client.idLegalClient),
                })),
              ]);
            }}
          />
          <Box sx={{ mt: 1, maxWidth: 180 }}>
            <CustomeTextField
              attr='Total'
              label='Summe'
              object={invoice}
              setObject={setInvoice}
              type='euro'
              maxWidth={180}
            />
          </Box>
        </TableCell>

        {/* Dateiname */}
        <TableCell sx={{ width: 240, maxWidth: 240 }}>
          <Tooltip title={localDocumentObject.FileName}>
            <Typography
              variant='body2'
              noWrap
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {localDocumentObject.FileName}
            </Typography>
          </Tooltip>
        </TableCell>

        {/* Aktionen */}
        <TableCell sx={{ width: 140 }}>
          <Tooltip title='Bild-in-Bild öffnen'>
            <IconButton
              onClick={async () => {
                if (localDocumentObject.Data) {
                  setSelectedSidepanelDocument(localDocumentObject);
                } else {
                  setSelectedSidepanelDocument(localDocumentObject);
                  getFetch(
                    '/document/',
                    localDocumentObject.idDocument,
                    (doc: IDocument) => {
                      setLocalDocumentObject(doc);
                      setSelectedSidepanelDocument(doc);
                    }
                  );
                }
              }}
            >
              <PictureInPicture />
            </IconButton>
          </Tooltip>

          <Tooltip title='Vorschau'>
            <IconButton size='small' onClick={handleOpenPreview}>
              <FindInPage />
            </IconButton>
          </Tooltip>

          {localDocumentObject.idDocument > 0 && downloadedArray && (
            <Tooltip title='Downloads'>
              <IconButton
                size='small'
                onClick={() => setIsOpenDownloaded(true)}
              >
                <Search />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title='Herunterladen'>
            <IconButton size='small' onClick={handleDownload}>
              <Download />
            </IconButton>
          </Tooltip>

          <Tooltip title='Löschen'>
            <IconButton size='small' onClick={handleRemove}>
              <Delete />
            </IconButton>
          </Tooltip>

          {!!(scopes.includes('bill') || scopes.includes('admin')) && (
            <Tooltip title='Zur Rechnung'>
              <IconButton
                size='small'
                onClick={() =>
                  window.open(`/invoices/${invoice.idInvoice}`, '_blank')
                }
              >
                <Link />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
    </>
  );
};
